"use client";

import { useLocale } from "next-intl";
import { useEffect, useLayoutEffect } from "react";

import { setAxiosLocale } from "@/libs/axios";
import { setAxiosCMSLocale } from "@/libs/axios-cms-client";

import { DEFAULT_FALLBACK_LOCALE } from "./config";
import { getWizardLanguageByCode } from "./wizard-helpers";

/**
 * Initializes axios clients (CMS and BFF) with current locale
 * Must be rendered as a child of NextIntlClientProvider
 *
 * This component bridges the gap between next-intl's locale context
 * and axios interceptors, allowing us to use HttpOnly cookies for security
 * while still having locale available for API calls.
 *
 * Uses useLayoutEffect to set locale synchronously before child components render,
 * preventing race conditions where API calls happen before locale is cached.
 */
export default function AxiosLocaleInitializer() {
  const locale = useLocale();

  // Use useLayoutEffect to set locale BEFORE child components render
  // This prevents race conditions with early API calls in useEffect hooks
  // Falls back to useEffect on server-side (where useLayoutEffect throws warning)
  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    // Set locale for CMS client
    setAxiosCMSLocale(locale);

    // Set locale for BFF client with backend locale code
    const setBackendLocale = async () => {
      try {
        const languageConfig = await getWizardLanguageByCode(locale);
        const backendLocaleCode =
          languageConfig?.backendLocaleCode ?? DEFAULT_FALLBACK_LOCALE;
        setAxiosLocale(locale, backendLocaleCode);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          "[AxiosLocaleInitializer] Error getting language config:",
          error
        );
        // Fallback to the locale if wizard config fails
        setAxiosLocale(locale, locale);
      }
    };

    setBackendLocale();
  }, [locale]);

  return null;
}
